import clsx from 'clsx';
import {
  CalendarDays,
  CalendarRange,
  Search,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/app/store';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import type { SyncStatus } from '@/domain/types';
import { resolveAvatarUrl } from '@/platform/avatars';
import styles from './Sidebar.module.css';

const NAV_ITEMS = [
  { key: 'today' as const, path: '/app/today', icon: Sun, labelKey: 'nav.today' },
  { key: 'calendar' as const, path: '/app/calendar', icon: CalendarDays, labelKey: 'nav.calendar' },
  { key: 'month' as const, path: '/app/month', icon: CalendarRange, labelKey: 'nav.monthPlans' },
  { key: 'shared' as const, path: '/app/shared', icon: Users, labelKey: 'nav.shared' },
  { key: 'search' as const, path: '/app/search', icon: Search, labelKey: 'nav.search' },
  { key: 'settings' as const, path: '/app/settings', icon: Settings, labelKey: 'nav.settings' },
];

function statusTone(
  paired: boolean,
  online: boolean,
  syncStatus: SyncStatus,
): 'positive' | 'warning' | 'accent' | 'danger' {
  if (!paired) return 'warning';
  if (!online || syncStatus === 'offline') return 'warning';
  if (syncStatus === 'sync_error') return 'danger';
  if (syncStatus === 'retrying' || syncStatus === 'syncing' || syncStatus === 'changes_pending') {
    return 'accent';
  }
  return 'positive';
}

function statusLabel(
  paired: boolean,
  online: boolean,
  syncStatus: SyncStatus,
  t: (key: string) => string,
): string {
  if (!paired) return t('app.pairSpace');
  if (!online || syncStatus === 'offline') return t('common.offline');
  if (syncStatus === 'sync_error') return t('app.sync.syncErrorShort');
  if (syncStatus === 'retrying') return t('app.sync.retrying');
  if (syncStatus === 'syncing') return t('app.sync.syncing');
  if (syncStatus === 'changes_pending') return t('app.sync.changesPending');
  return t('common.online');
}

export interface SidebarProps {
  compact?: boolean;
}

export function Sidebar({ compact = false }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useAppStore((s) => s.session);
  const profiles = useAppStore((s) => s.profiles);
  const online = useAppStore((s) => s.online);
  const syncStatus = useAppStore((s) => s.syncStatus);
  const setMobileTab = useAppStore((s) => s.setMobileTab);
  const syncNow = useAppStore((s) => s.syncNow);

  const me = profiles.find((p) => p.id === session?.user.id);
  const partner = profiles.find((p) => p.id !== session?.user.id);
  const paired = profiles.length >= 2;

  const toMobileTab = (
    key: (typeof NAV_ITEMS)[number]['key'],
  ): 'today' | 'calendar' | 'month' | 'shared' | 'search' | 'settings' =>
    key === 'month' ? 'month' : key;

  const handleNav = (key: typeof NAV_ITEMS[number]['key'], path: string) => {
    setMobileTab(toMobileTab(key));
    navigate(path);
  };

  const handleSyncBadgeClick = () => {
    if (
      syncStatus === 'sync_error' ||
      syncStatus === 'changes_pending' ||
      syncStatus === 'retrying'
    ) {
      void syncNow();
    }
  };

  return (
    <aside className={clsx('chrome-surface', styles.sidebar, compact && styles.compact)}>
      <nav className={styles.nav} aria-label={t('shortcuts.navigate')}>
        {NAV_ITEMS.map(({ key, path, icon: Icon, labelKey }) => (
          <NavLink
            key={key}
            to={path}
            end={path === '/app/calendar'}
            className={({ isActive }) =>
              clsx(styles.navItem, isActive && styles.active)
            }
            onClick={() => setMobileTab(toMobileTab(key))}
          >
            <Icon size={18} aria-hidden="true" />
            <span className={styles.label}>{t(labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.profileBlock}
          onClick={() => handleNav('settings', '/app/settings')}
        >
          {me && (
            <Avatar name={me.displayName} color={me.accentColor} size="sm" src={resolveAvatarUrl(me.avatarPath, me.updatedAt) ?? undefined} />
          )}
          <div className={styles.profileText}>
            <span className={styles.name}>{me?.displayName ?? '—'}</span>
            {partner ? (
              <span className={styles.partner}>
                <span className={styles.partnerDot} aria-hidden="true" />
                {t('app.pairedWith', { name: partner.displayName })}
              </span>
            ) : (
              <span className={styles.partnerMuted}>{t('app.waitingForPartner')}</span>
            )}
          </div>
        </button>
        <button
          type="button"
          className={styles.statusBadge}
          onClick={handleSyncBadgeClick}
          title={statusLabel(paired, online, syncStatus, t)}
        >
          <Badge tone={statusTone(paired, online, syncStatus)}>
            {statusLabel(paired, online, syncStatus, t)}
          </Badge>
        </button>
        <span className={styles.spaceLabel}>{t('app.spaceLabel')}</span>
      </div>
    </aside>
  );
}
